// Validación pura de la identidad de sesión (R5.3 / D9).
//
// `/api/auth/me` (y `/api/auth/login`) devuelven el mismo objeto de usuario
// del backend (`{k: v for k, v in user.items() if k != "password"}`). Un 2xx
// NO garantiza una identidad válida: un proxy, un backend con un bug o un
// payload `{}` producirían una "sesión falsa" (usuario sin id, rol o tenant)
// que `ProtectedRoute` dejaría pasar.
//
// Contrato vigente (backend `UserBase`):
//   role     ∈ {SUPER_ADMIN, CENTER_ADMIN, STAFF, CLIENT}
//   centerId presente para todo rol salvo SUPER_ADMIN
//   id       obligatorio
//
// Los tres campos del contrato se leen como PROPIEDADES PROPIAS del payload:
// un valor heredado del prototype chain (p. ej. tras contaminar
// `Object.prototype`) NO forma parte de la identidad y se trata como AUSENTE
// ⇒ el payload se rechaza (fail-closed).
//
// La función NO transforma el payload: si es válido devuelve el MISMO objeto
// (para no perder `email`, `fullName`, `language`, `allowedSections`,
// `serviceIds`, etc.). Si es inválido devuelve `user: null` y un motivo.

export const AUTH_ROLES = Object.freeze([
  'SUPER_ADMIN',
  'CENTER_ADMIN',
  'STAFF',
  'CLIENT',
]);

export const AUTH_IDENTITY_REASONS = Object.freeze({
  NOT_AN_OBJECT: 'not-an-object',
  MISSING_ID: 'missing-id',
  UNKNOWN_ROLE: 'unknown-role',
  MISSING_CENTER_ID: 'missing-center-id',
});

// Tipos del estado de verificación de sesión (`authVerificationError`).
//
//  - 'unverified'       → fallo TRANSITORIO (red, timeout, 5xx, 403): la causa
//                         no es el payload; la credencial se conserva y la
//                         acción principal es "Reintentar".
//  - 'invalid-identity' → rechazo DETERMINISTA del payload (2xx sin identidad
//                         válida): la causa es el contenido de la respuesta,
//                         no la conexión. La credencial se conserva (no es un
//                         401 autoritativo) pero el mismo payload volverá a
//                         ser rechazado, así que el panel expone el motivo y
//                         una salida explícita a `/login`.
export const AUTH_VERIFICATION_KINDS = Object.freeze({
  UNVERIFIED: 'unverified',
  INVALID_IDENTITY: 'invalid-identity',
});

// Mensaje legible por motivo de rechazo. Los `reason` son un enum cerrado de
// cadenas fijas: no contienen payload, stack, token ni datos personales.
export const AUTH_IDENTITY_REASON_MESSAGES = Object.freeze({
  [AUTH_IDENTITY_REASONS.NOT_AN_OBJECT]:
    'La respuesta del servidor no contiene una identidad de usuario.',
  [AUTH_IDENTITY_REASONS.MISSING_ID]:
    'La respuesta del servidor no incluye el identificador de usuario.',
  [AUTH_IDENTITY_REASONS.UNKNOWN_ROLE]:
    'La respuesta del servidor incluye un rol no reconocido.',
  [AUTH_IDENTITY_REASONS.MISSING_CENTER_ID]:
    'La respuesta del servidor no incluye el centro asignado a tu usuario.',
});

/**
 * Lee una PROPIEDAD PROPIA del payload.
 *
 * Un valor heredado del prototype chain (p. ej. tras la contaminación de
 * `Object.prototype`) NO forma parte de la identidad: si se aceptara, un
 * payload envenenado podría concederse el rol `SUPER_ADMIN` —o el `centerId`
 * de otro centro— sin que el servidor lo hubiera enviado nunca. Por eso los
 * tres campos del contrato (`id`, `role`, `centerId`) se leen SIEMPRE como
 * propiedades propias: si el campo no es propio, se trata como AUSENTE.
 */
function ownProperty(payload, key) {
  if (!Object.prototype.hasOwnProperty.call(payload, key)) return undefined;
  return payload[key];
}

/**
 * Valida la identidad devuelta por el backend.
 * @returns {{valid: boolean, reason: string|null, user: object|null}}
 */
export function validateAuthIdentity(payload) {
  const invalid = (reason) => ({ valid: false, reason, user: null });

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return invalid(AUTH_IDENTITY_REASONS.NOT_AN_OBJECT);
  }

  // `id`, `role` y `centerId` se leen como propiedades PROPIAS (D9): un valor
  // heredado del prototype chain se trata como ausente y el payload se
  // rechaza.
  const rawId = ownProperty(payload, 'id');
  const id = typeof rawId === 'string' ? rawId.trim() : '';
  if (!id) {
    return invalid(AUTH_IDENTITY_REASONS.MISSING_ID);
  }

  const role = ownProperty(payload, 'role');
  if (!AUTH_ROLES.includes(role)) {
    return invalid(AUTH_IDENTITY_REASONS.UNKNOWN_ROLE);
  }

  // Todo rol salvo SUPER_ADMIN está atado a un centro (aislamiento
  // multi-tenant). SUPER_ADMIN NO necesita centerId y NO se le infiere ni se
  // le amplía el tenant.
  if (role !== 'SUPER_ADMIN') {
    const rawCenterId = ownProperty(payload, 'centerId');
    const centerId =
      typeof rawCenterId === 'string' ? rawCenterId.trim() : '';
    if (!centerId) {
      return invalid(AUTH_IDENTITY_REASONS.MISSING_CENTER_ID);
    }
  }

  return { valid: true, reason: null, user: payload };
}
